import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../api";
import { useTranslation } from "../../../hooks/useTranslation";
import { useInView } from "../../../hooks/useInView";
import usePostHog from "../../../AppContent/posthog";
import { citiesMatch, placeMoodPath } from "../../../utils/places";
import { VIBES, vibeLabelKey, VIBE_CITY_MIN } from "../../../Config/vibes";
import "../../Places/VibePills.css";

/**
 * Homepage "What are you in the mood for?" entry point — the vibe filter as
 * primary navigation (Spotify / The Nudge style) rather than a control
 * buried on the list page. Renders one pill per vibe that has at least
 * VIBE_CITY_MIN Top Picks in the selected city, each linking to that vibe's
 * /places/:city/mood/:vibe landing page.
 *
 * Until it knows there's something to show it renders only a zero-height
 * observer anchor (so the fetch still fires when scrolled near) — no empty
 * section band flashes on the homepage for a city with no qualifying vibes,
 * matching the "no content, no section" rule the other homepage sections
 * follow. No sp-reveal scroll-in wrapper here: the section sits directly
 * under the hero, and that wrapper only mounts after the fetch resolves,
 * which is too late for useInView's one-shot observer to ever attach to it.
 */
const MoodSection = ({ selectedCity }) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const [ref, isInView] = useInView(0, "250px 0px");
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    if (!isInView) return;
    api
      .getPublicCompanies()
      .then((res) => setCompanies(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Error fetching companies for mood section:", err));
  }, [isInView]);

  const availableVibes = useMemo(() => {
    if (!selectedCity) return [];
    const cityPicks = companies.filter(
      (c) => c.isTopPick && citiesMatch(c.city, selectedCity)
    );
    return VIBES.filter(
      (v) => cityPicks.filter((c) => (c.vibes || []).includes(v.slug)).length >= VIBE_CITY_MIN
    );
  }, [companies, selectedCity]);

  // A "pick your mood" module with one option isn't a picker — need at least
  // two qualifying vibes in the city before this earns a spot on the home
  // page. (The list-page filter and the /mood/:vibe pages have no such floor
  // — they always carry the "All" pill / full context.)
  if (availableVibes.length < 2) {
    return <div ref={ref} aria-hidden="true" style={{ height: 0 }} />;
  }

  return (
    <section
      className="sp-section"
      style={{ background: "var(--color-surface)", paddingLeft: 32, paddingRight: 32 }}
    >
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        <div className="tag tag-accent">{t.vibeHomeKicker}</div>
        <h2 style={{ marginTop: 10, maxWidth: 560 }}>{t.vibeHomeHeading}</h2>
        <p style={{ opacity: 0.75, maxWidth: 520 }}>{t.vibeHomeSubtitle}</p>

        <div className="sp-vibe-pills" style={{ marginTop: 22 }}>
          {availableVibes.map((v) => (
            <Link
              key={v.slug}
              to={placeMoodPath(selectedCity, v.slug)}
              className="sp-vibe-pill"
              onClick={() =>
                posthog.capture("vibe_pill_clicked", {
                  vibe: v.slug,
                  city: selectedCity,
                  source: "home",
                })
              }
            >
              {v.emoji} {t[vibeLabelKey(v.slug)] || v.slug}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MoodSection;
